import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { format } from 'date-fns';
import { APP_NAVIGATION_LINKS } from 'general/links';
import hexRgb from 'hex-rgb';

import {
  IBrandingSettings,
  IDocumentBreadcrumb,
  IDocumentationCategory,
  IFeatureSettings,
  INavigationLink,
} from '../types/platform.types';

export const defaultDocumentationBreadcrumb = [
  {
    name: 'Platform Documentation',
    slug: '',
    isCurrent: true,
  },
];

export interface InitialState {
  isLoading: boolean;
  navigationLinks: INavigationLink[];
  documentationBreadcrumb: IDocumentBreadcrumb[];
  currentDocumentationCategories: IDocumentationCategory[];
  currentDocumentationArticles: IDocumentationCategory[];
  currentDocumentationContent: string | null;
  currentDocumentationTitle: string | null;
  brandingSettings: IBrandingSettings;
  featureSettings: IFeatureSettings;
}

const initialState: InitialState = {
  isLoading: false,
  featureSettings: {
    default_forward_delivery_date: format(new Date(new Date().getFullYear(), 0, 31, 12), 'yyyy-MM-dd'),
    default_login_page: '/eecs-go',
    main_company: null,
    disabled_pages: [],
    as_buyer_fee: 0,
    as_seller_fee: 0,
    product_definitions: '',
  },
  navigationLinks: APP_NAVIGATION_LINKS,
  documentationBreadcrumb: defaultDocumentationBreadcrumb,
  currentDocumentationCategories: [],
  currentDocumentationArticles: [],
  currentDocumentationContent: null,
  currentDocumentationTitle: 'Platform Documentation',
  brandingSettings: {
    company_name: 'Loading...',
    accent_color: '#168695',
    accentColorRGB: {
      r: 22,
      g: 134,
      g10: 124,
      b: 149,
    },
    accent_text_color: '#000000',
    logo: null,
    favicon: null,
    qr_code_logo: null,
    main_domain_name: '',
    marketplace_motto: '',
    admin_panel_name_alias: '',
    marketplace_name_alias: '',
  },
};

export const platform = createSlice({
  name: 'platform',
  initialState: initialState,
  reducers: {
    setIsLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    setNavigationLinks(state, action: PayloadAction<INavigationLink[]>) {
      state.navigationLinks = action.payload;
    },
    setDocumentationBreadcrumb(state, action: PayloadAction<IDocumentBreadcrumb[]>) {
      state.documentationBreadcrumb = action.payload;
    },
    setCurrentDocumentationCategories(state, action: PayloadAction<IDocumentationCategory[]>) {
      state.currentDocumentationCategories = action.payload;
    },
    setCurrentDocumentationArticles(state, action: PayloadAction<IDocumentationCategory[]>) {
      state.currentDocumentationArticles = action.payload;
    },
    setCurrentDocumentationContent(state, action: PayloadAction<string | null>) {
      state.currentDocumentationContent = action.payload;
    },
    setCurrentDocumentationTitle(state, action: PayloadAction<string | null>) {
      state.currentDocumentationTitle = action.payload;
    },
    setBrandingSettings(state, action: PayloadAction<IBrandingSettings>) {
      const accentColor = action.payload.accent_color || state.brandingSettings.accent_color;
      const accentColorRGB = {
        r: hexRgb(accentColor).red,
        g: hexRgb(accentColor).green,
        g10: hexRgb(accentColor).green - 10,
        b: hexRgb(accentColor).blue,
      };
      state.brandingSettings = { ...action.payload, accentColorRGB };
    },
    setFeatureSettings(state, action: PayloadAction<IFeatureSettings>) {
      state.featureSettings = action.payload;
    },
  },
});

export const {
  setIsLoading,
  setDocumentationBreadcrumb,
  setCurrentDocumentationCategories,
  setCurrentDocumentationArticles,
  setCurrentDocumentationContent,
  setCurrentDocumentationTitle,
  setBrandingSettings,
  setFeatureSettings,
  setNavigationLinks,
} = platform.actions;
